/**
 * BasketAdjustPanel — expand content for a running option basket.
 *
 * Shows constituent legs (strike / expiry / individual P&L / Greeks / moneyness),
 * the adjustment engine's verdict (needed? + reasons), and — for tested credit /
 * defined-risk structures — 2-3 candidate ROLLS, each with its projected payoff
 * and economics. The user can fine-tune a candidate's new legs (strike / expiry),
 * watch the payoff update, and approve to execute the adjustment.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Button, Divider, Table, TableHead, TableBody,
  TableRow, TableCell, IconButton, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from '@mui/material';
import toast from 'react-hot-toast';
import { optionsService } from '../../services/options.service';
import PayoffChartPro from '../charts/PayoffChartPro';
import { payoffFromFills } from '../../utils/payoffFromFills';

const fmtInr = (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}`;
const fmtSigned = (v: number | null | undefined) => (v == null ? '—' : `${v >= 0 ? '+' : '−'}${fmtInr(Math.abs(v))}`);
const num = (v: any, d = 2) => (v == null || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(d));

type Leg = { symbol: string; action: 'BUY' | 'SELL'; optionType: 'CE' | 'PE'; strike: number; expiry: string; quantity: number; entryFill: number | null; mark: number | null; pnlInr: number | null; delta: number | null; iv: number | null; moneyness: string; distancePct: number; dteHours: number | null };
type Candidate = { id: string; label: string; rationale: string; closeLegs: any[]; openLegs: any[]; bookedPnlInr: number; projected: any };
type Data = { legs: Leg[]; verdict: any; candidates: Candidate[]; chain: any; live: any; adjustmentsRealizedPnl: number; adjustmentLog: any[] };

const sevColor: Record<string, 'default' | 'info' | 'warning' | 'error'> = { none: 'default', info: 'info', warning: 'warning', urgent: 'error' };

const BasketAdjustPanel: React.FC<{ basketId: number; strategy: string; tradingMode?: 'PAPER' | 'LIVE'; contractValue?: number | null; onAdjusted: () => void }> = ({ basketId, strategy, tradingMode, contractValue, onAdjusted }) => {
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState<any[] | null>(null); // edited open legs for the selected candidate
  const [editProjected, setEditProjected] = useState<any | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r: any = await optionsService.getBasketAdjustment(basketId);
      const d: Data = r?.data?.data;
      setData(d);
      setSelId(d?.candidates?.[0]?.id || null);
      setEditOpen(null); setEditProjected(null);
    } catch (e: any) { setErr(e?.response?.data?.error?.message || e?.message || 'Could not analyse'); }
    finally { setLoading(false); }
  }, [basketId]);
  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => data?.candidates.find(c => c.id === selId) || null, [data, selId]);
  // The legs that survive the chosen adjustment (not closed) — for re-preview.
  const survivors = useMemo(() => {
    if (!data || !selected) return [];
    const closeSet = new Set(selected.closeLegs.map((c: any) => c.symbol));
    return data.legs.filter(l => !closeSet.has(l.symbol)).map(l => ({ action: l.action, optionType: l.optionType, strike: l.strike, expiry: l.expiry }));
  }, [data, selected]);
  const base = useMemo(() => String(data?.legs?.[0]?.symbol || '').split('-')[1] || '', [data]);
  const strategyId = useMemo(() => strategy.toLowerCase().replace(/\s+/g, '_'), [strategy]);

  // CURRENT-position payoff (at expiry) from the actual ENTRY fills — the P&L the
  // running basket is really carrying, shown even when no adjustment is pending. Delta
  // legs ({C|P}-…) are USD-native (×85); NSE legs are ₹-native (fx 1).
  const currentCard = useMemo(() => {
    if (!data?.legs?.length || !(Number(contractValue) > 0)) return null;
    const isDelta = /^[CP]-/.test(String(data.legs[0].symbol || ''));
    return payoffFromFills(
      data.legs.map(l => ({ action: l.action, optionType: l.optionType, strike: Number(l.strike), entryFill: l.entryFill, quantity: Number(l.quantity) })),
      Number(contractValue),
      data.live?.spot ?? null,
      isDelta ? 85 : 1,
    );
  }, [data, contractValue]);

  // When a candidate is selected, seed the editable open legs from it.
  useEffect(() => { if (selected) { setEditOpen(selected.openLegs.map((o: any) => ({ ...o }))); setEditProjected(null); } }, [selId]); // eslint-disable-line

  const reprice = useCallback(async (openLegs: any[]) => {
    if (!base) return;
    setPreviewing(true);
    try {
      const full = [...survivors, ...openLegs.map(o => ({ action: o.action, optionType: o.optionType, strike: o.strike, expiry: o.expiry }))];
      const r: any = await optionsService.previewBasket({ underlyingSymbol: base, strategyId, legs: full });
      const card = r?.data?.data?.card;
      if (card) {
        // Re-resolve each open leg's symbol/strike from the previewed card (snapping).
        const cardLegs: any[] = card.legs || [];
        const resolved = openLegs.map(o => {
          const cands = cardLegs.filter(cl => cl.optionType === o.optionType && cl.action === o.action && String(cl.expiry).slice(0, 10) === String(o.expiry).slice(0, 10));
          let best: any = null; for (const cl of cands) if (!best || Math.abs(cl.strike - o.strike) < Math.abs(best.strike - o.strike)) best = cl;
          return best ? { ...o, strike: Number(best.strike), symbol: best.symbol } : o;
        });
        setEditOpen(resolved); setEditProjected(card);
      }
    } catch { /* keep prior */ }
    finally { setPreviewing(false); }
  }, [base, strategyId, survivors]);

  const stepStrike = (idx: number, dir: 1 | -1) => {
    if (!editOpen || !data?.chain) return;
    const step = Number(data.chain.strikeStep) || 1;
    const next = editOpen.map((o, i) => i === idx ? { ...o, strike: o.strike + dir * step } : o);
    setEditOpen(next); reprice(next);
  };
  const setExpiry = (idx: number, exp: string) => {
    if (!editOpen) return;
    const next = editOpen.map((o, i) => i === idx ? { ...o, expiry: exp } : o);
    setEditOpen(next); reprice(next);
  };

  const projected = editProjected || selected?.projected || null;
  const openLegsToExecute = editOpen || selected?.openLegs || [];

  const doAdjust = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await optionsService.adjustBasket(basketId, { closeLegs: selected.closeLegs, openLegs: openLegsToExecute });
      toast.success(`Adjustment placed on basket #${basketId}.`, { duration: 7000 });
      setConfirm(false); onAdjusted();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.message || 'Adjustment failed', { duration: 8000 });
    } finally { setBusy(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, px: 1 }}><CircularProgress size={16} /> <Typography variant="caption" color="text.secondary">Analysing legs & adjustment…</Typography></Box>;
  if (err) return <Alert severity="warning" sx={{ my: 1 }}>{err}</Alert>;
  if (!data) return null;

  const v = data.verdict;
  const dte = (h: number | null) => h == null ? '—' : h >= 24 ? `${Math.floor(h / 24)}d ${Math.floor(h % 24)}h` : `${Math.floor(h)}h`;

  return (
    <Box sx={{ p: 1, bgcolor: 'rgba(127,127,127,0.04)', borderRadius: 1.5 }}>
      {/* ── Verdict ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={v.needed ? `Adjustment ${v.severity}` : 'No adjustment needed'} color={v.needed ? sevColor[v.severity] : 'success'} sx={{ height: 20, fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase' }} />
        {v.testedSide && <Typography variant="caption" color="text.secondary">tested side: <strong>{v.testedSide}</strong></Typography>}
        {data.adjustmentsRealizedPnl ? <Chip size="small" label={`Adj. booked ${fmtSigned(data.adjustmentsRealizedPnl)}`} sx={{ height: 18, fontSize: '0.58rem', ml: 'auto' }} /> : null}
      </Box>
      {v.reasons?.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {v.reasons.map((r: string, i: number) => <Typography key={i} variant="caption" sx={{ display: 'block', color: v.needed ? 'text.primary' : 'text.secondary', lineHeight: 1.5 }}>• {r}</Typography>)}
        </Box>
      )}

      {/* ── Leg detail ── */}
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { px: 0.8, py: 0.4, fontSize: '0.68rem', whiteSpace: 'nowrap' } }}>
          <TableHead><TableRow>
            {['Leg', 'Strike', 'Expiry', 'DTE', 'Qty', 'Entry', 'Mark', 'P&L', 'Δ', 'IV', ''].map(h => <TableCell key={h} sx={{ fontWeight: 700, color: 'text.secondary' }}>{h}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {[...data.legs].sort((a, b) => b.strike - a.strike).map((l, i) => (
              <TableRow key={i}>
                <TableCell><Chip size="small" label={`${l.action === 'BUY' ? '+' : '−'}${l.optionType}`} sx={{ height: 16, fontSize: '0.56rem', fontWeight: 800, bgcolor: l.action === 'SELL' ? '#fdecea' : '#e8f5e9', color: l.action === 'SELL' ? '#c62828' : '#2e7d32' }} /></TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{l.strike.toLocaleString('en-IN')}</TableCell>
                <TableCell>{l.expiry?.slice(5)}</TableCell>
                <TableCell>{dte(l.dteHours)}</TableCell>
                <TableCell>{l.quantity}</TableCell>
                <TableCell>{num(l.entryFill)}</TableCell>
                <TableCell>{num(l.mark)}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: (l.pnlInr ?? 0) >= 0 ? '#2e7d32' : '#c62828' }}>{fmtSigned(l.pnlInr)}</TableCell>
                <TableCell sx={{ color: l.moneyness === 'ITM' ? '#c62828' : 'text.secondary' }}>{num(l.delta)}</TableCell>
                <TableCell>{l.iv != null ? `${Math.round(l.iv * 100)}%` : '—'}</TableCell>
                <TableCell><Typography variant="caption" sx={{ fontSize: '0.56rem', color: l.moneyness === 'ITM' ? '#c62828' : 'text.disabled' }}>{l.moneyness}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Net Δ {num(v.netDelta)} · Θ {num(v.netTheta)} · ν {num(v.netVega)} · spot {data.live?.spot != null ? Math.round(data.live.spot).toLocaleString('en-IN') : '—'}
      </Typography>

      {/* ── Current-position payoff (at expiry, from actual entry fills) ── */}
      {currentCard && (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.4 }}>
            <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary' }}>Current position payoff</Typography>
            <Typography sx={{ fontSize: '0.56rem', color: 'text.disabled' }}>P&amp;L at expiry vs {base || 'underlying'} — from your entry fills</Typography>
          </Box>
          <Box sx={{ bgcolor: 'rgba(127,127,127,0.06)', borderRadius: 1, p: 0.5 }}>
            <PayoffChartPro basket={currentCard} spotPrice={data.live?.spot || 0} underlyingSymbol={base} />
          </Box>
        </Box>
      )}

      {/* ── Candidates ── */}
      {data.candidates.length > 0 ? (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5 }}>Suggested adjustments</Typography>
          <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mb: 1 }}>
            {data.candidates.map(c => (
              <Chip key={c.id} size="small" label={c.label} onClick={() => setSelId(c.id)} variant={selId === c.id ? 'filled' : 'outlined'} color={selId === c.id ? 'primary' : 'default'} sx={{ height: 24, fontWeight: 700, fontSize: '0.66rem', cursor: 'pointer' }} />
            ))}
          </Box>
          {selected && (
            <Box>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, lineHeight: 1.5 }}>{selected.rationale}</Typography>
              {/* Editable new legs */}
              {(editOpen || []).length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'text.secondary', mb: 0.3 }}>NEW LEGS {previewing && <CircularProgress size={10} sx={{ ml: 0.5 }} />}</Typography>
                  {editOpen!.map((o, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, py: 0.2, flexWrap: 'wrap' }}>
                      <Chip size="small" label={`${o.action === 'BUY' ? '+' : '−'}${o.optionType}`} sx={{ height: 16, fontSize: '0.56rem', fontWeight: 800 }} />
                      <IconButton size="small" onClick={() => stepStrike(i, -1)} disabled={previewing} sx={{ p: 0.1, border: '1px solid', borderColor: 'divider' }}>−</IconButton>
                      <Typography sx={{ fontWeight: 800, minWidth: 56, textAlign: 'center', fontSize: '0.74rem' }}>{o.strike.toLocaleString('en-IN')}</Typography>
                      <IconButton size="small" onClick={() => stepStrike(i, 1)} disabled={previewing} sx={{ p: 0.1, border: '1px solid', borderColor: 'divider' }}>+</IconButton>
                      <Select size="small" value={o.expiry?.slice(0, 10)} onChange={e => setExpiry(i, String(e.target.value))} variant="standard" sx={{ fontSize: '0.68rem', minWidth: 90 }}>
                        {(data.chain?.availableExpiries || [o.expiry]).map((e: string) => <MenuItem key={e} value={e.slice(0, 10)} sx={{ fontSize: '0.72rem' }}>{e.slice(0, 10)}</MenuItem>)}
                      </Select>
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem' }}>×{o.quantity}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
              {/* Projected payoff + economics */}
              {projected && (
                <>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 0.5 }}>
                    <Stat label={Number(projected.netPremium) < 0 ? 'Net credit' : 'Net debit'} value={fmtInr(Math.abs(Number(projected.netPremium || 0)))} color={Number(projected.netPremium) < 0 ? '#2e7d32' : '#ed6c02'} />
                    <Stat label="Max profit" value={fmtInr(Number(projected.maxProfit || 0))} color="#2e7d32" />
                    <Stat label="Max loss" value={Math.abs(Number(projected.maxLoss || 0)) === 0 ? 'Undefined' : fmtInr(Math.abs(Number(projected.maxLoss || 0)))} color="#c62828" />
                    <Stat label="Margin" value={fmtInr(Number(projected.marginEstimate || 0))} />
                    <Stat label="Booked on close" value={fmtSigned(selected.bookedPnlInr)} color={selected.bookedPnlInr >= 0 ? '#2e7d32' : '#c62828'} />
                  </Box>
                  <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary', mb: 0.4 }}>Payoff after adjustment</Typography>
                  <Box sx={{ bgcolor: 'rgba(127,127,127,0.06)', borderRadius: 1, p: 0.5 }}>
                    <PayoffChartPro basket={projected} spotPrice={data.live?.spot || 0} underlyingSymbol={base} />
                  </Box>
                </>
              )}
              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  Approving closes {selected.closeLegs.length} leg(s) and opens {openLegsToExecute.length} new leg(s){tradingMode === 'PAPER' ? ' (PAPER — simulated)' : ' (real reduce-only + new orders, gated)'}, then re-baselines TP/SL.
                </Typography>
                <Button size="small" variant="contained" color="primary" disabled={busy || previewing || !projected} onClick={() => setConfirm(true)}>Approve &amp; adjust</Button>
              </Box>
            </Box>
          )}
        </>
      ) : v.supported ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>No candidate roll could be priced on the current chain right now.</Typography>
      ) : null}

      <Dialog open={confirm} onClose={() => { if (!busy) setConfirm(false); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>Adjust basket #{basketId}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{selected?.label}</strong> — close {selected?.closeLegs.length} leg(s), open {openLegsToExecute.length} new leg(s).
            {tradingMode === 'PAPER' ? ' Simulated (PAPER).' : ' This places REAL orders on Delta (reduce-only closes + new opens), through your gates.'}
          </Typography>
          {projected && <Typography variant="body2" sx={{ mt: 1 }}>After: max loss {fmtInr(Math.abs(Number(projected.maxLoss || 0)))}, {Number(projected.netPremium) < 0 ? 'credit' : 'debit'} {fmtInr(Math.abs(Number(projected.netPremium || 0)))}. Booked on close ≈ {fmtSigned(selected?.bookedPnlInr)}.</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirm(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={doAdjust} disabled={busy} startIcon={busy ? <CircularProgress size={15} color="inherit" /> : undefined}>{busy ? 'Adjusting…' : 'Confirm adjust'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Stat: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Box>
    <Typography sx={{ fontSize: '0.52rem', fontWeight: 700, letterSpacing: 0.3, color: 'text.secondary', textTransform: 'uppercase' }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: color || 'text.primary' }}>{value}</Typography>
  </Box>
);

export default BasketAdjustPanel;
